/* ==========================================================================
   BEHIND - CUSTOM SCRIPTS
   Custom code for Behind. Kept separate from the Prestige core files.
   Loaded from layout/theme.liquid.

   1. collection-view-switch: toggles the collection grid between the product
      image and the model image (third product image). The choice is remembered
      in localStorage, the default comes from the theme editor setting.
   2. Notify when available popup: opened from the button that replaces the
      sold out button on the product page. The form is sent in the background
      so the visitor sees the confirmation without the page reloading.
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     1. COLLECTION VIEW SWITCH
     ---------------------------------------------------------------------- */

  var STORAGE_KEY = 'behind-collection-view';

  function readStoredView(key) {
    try {
      var value = window.localStorage.getItem(key);
      return value === 'model' || value === 'product' ? value : null;
    } catch (error) {
      return null;
    }
  }

  function writeStoredView(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      /* Storage can be unavailable (private mode). The switch keeps working for this page. */
    }
  }

  if (window.customElements && !window.customElements.get('collection-view-switch')) {
    var CollectionViewSwitch = function () {
      return Reflect.construct(HTMLElement, [], CollectionViewSwitch);
    };

    CollectionViewSwitch.prototype = Object.create(HTMLElement.prototype);
    CollectionViewSwitch.prototype.constructor = CollectionViewSwitch;
    Object.setPrototypeOf(CollectionViewSwitch, HTMLElement);

    CollectionViewSwitch.prototype.connectedCallback = function () {
      this.storageKey = this.getAttribute('storage-key') || STORAGE_KEY;
      this.ignoreStorage = this.hasAttribute('ignore-storage');
      this.defaultView = this.getAttribute('default-view') === 'model' ? 'model' : 'product';
      this.buttons = Array.prototype.slice.call(this.querySelectorAll('button[value]'));

      var self = this;

      if (!this.hasBoundListeners) {
        this.hasBoundListeners = true;

        this.buttons.forEach(function (button) {
          button.addEventListener('click', function () {
            self.applyView(button.value, true);
          });
        });
      }

      var initialView = this.ignoreStorage ? this.defaultView : readStoredView(this.storageKey) || this.defaultView;

      this.applyView(initialView, false);
    };

    CollectionViewSwitch.prototype.resolveTarget = function () {
      var targetId = this.getAttribute('target');
      var target = targetId ? document.getElementById(targetId) : null;

      return target || this.closest('.shopify-section') || this.closest('[data-collection-view]') || document.body;
    };

    CollectionViewSwitch.prototype.applyView = function (view, persist) {
      var normalized = view === 'model' ? 'model' : 'product';
      var target = this.resolveTarget();

      if (target) {
        target.setAttribute('data-collection-view', normalized);
      }

      this.buttons.forEach(function (button) {
        var isActive = button.value === normalized;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      if (persist && !this.ignoreStorage) {
        writeStoredView(this.storageKey, normalized);
      }
    };

    window.customElements.define('collection-view-switch', CollectionViewSwitch);
  }

  /* ------------------------------------------------------------------------
     2. NOTIFY WHEN AVAILABLE POPUP
     Delegated listeners, so the popup keeps working after a section is
     re-rendered (variant change, quick buy, filtering).
     ---------------------------------------------------------------------- */

  var openPopup = null;
  var previousOverflow = '';

  function releaseScrollLock() {
    document.documentElement.style.overflow = previousOverflow;
  }

  function showPopup(popup) {
    if (!popup || popup === openPopup) {
      return;
    }

    hidePopup();

    /* The product info column creates its own stacking context and the sticky add to cart bar sits on top
       of it, so the popup is moved to the end of the body before it is shown. */
    if (popup.parentElement !== document.body) {
      var duplicates = document.querySelectorAll('[id="' + popup.id + '"]');

      Array.prototype.forEach.call(duplicates, function (element) {
        if (element !== popup && element.parentElement === document.body) {
          element.parentElement.removeChild(element);
        }
      });

      document.body.appendChild(popup);
    }

    popup.hidden = false;
    openPopup = popup;
    previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';

    var field = popup.querySelector('input[type="email"]');

    if (field) {
      window.setTimeout(function () {
        field.focus();
      }, 50);
    }
  }

  function hidePopup() {
    if (!openPopup) {
      return;
    }

    openPopup.hidden = true;
    openPopup = null;
    releaseScrollLock();
  }

  function showSuccess(popup) {
    var fields = popup.querySelector('[data-custom-notify-fields]');
    var success = popup.querySelector('[data-custom-notify-success]');
    var failure = popup.querySelector('[data-custom-notify-error]');

    if (failure) {
      failure.hidden = true;
    }

    if (fields) {
      fields.hidden = true;
    }

    if (success) {
      success.hidden = false;
    }
  }

  function showError(popup, submitButton) {
    var failure = popup.querySelector('[data-custom-notify-error]');

    if (failure) {
      failure.hidden = false;
    }

    if (submitButton) {
      submitButton.disabled = false;
    }
  }

  document.addEventListener('click', function (event) {
    if (!event.target.closest) {
      return;
    }

    var trigger = event.target.closest('[data-custom-notify-open]');

    if (trigger) {
      var popupId = trigger.getAttribute('data-custom-notify-open');
      var popup = popupId ? document.getElementById(popupId) : null;

      if (popup) {
        event.preventDefault();
        showPopup(popup);
      }

      return;
    }

    if (event.target.closest('[data-custom-notify-close]')) {
      event.preventDefault();
      hidePopup();
    }
  });

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') {
      hidePopup();
    }
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;

    if (!form || !form.classList || !form.classList.contains('custom-notify-popup__form')) {
      return;
    }

    var popup = form.closest('.custom-notify-popup');

    if (!popup || typeof window.fetch !== 'function') {
      return;
    }

    event.preventDefault();

    var submitButton = form.querySelector('button[type="submit"]');

    if (submitButton) {
      submitButton.disabled = true;
    }

    window
      .fetch(form.action, {
        method: 'POST',
        body: new URLSearchParams(new FormData(form))
      })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Request failed');
        }

        return response.text();
      })
      .then(function (html) {
        /* Shopify answers with the page itself. A form error block means the address was refused. */
        if (html && html.indexOf('form-status--error') !== -1) {
          throw new Error('Form error');
        }

        showSuccess(popup);
      })
      .catch(function () {
        showError(popup, submitButton);
      });
  });

  /* The sticky add to cart bar builds its own button, so the sold out button there is upgraded to the
     same notify trigger. If the markup ever changes nothing happens and the original button stays. */

  function upgradeStickyBar() {
    var popup = document.querySelector('.custom-notify-popup');

    if (!popup) {
      return;
    }

    var label = popup.getAttribute('data-custom-notify-button-text');
    var bars = document.querySelectorAll('.product-sticky-bar');

    Array.prototype.forEach.call(bars, function (bar) {
      var button = bar.querySelector('button[disabled]');

      if (!button || button.hasAttribute('data-custom-notify-open')) {
        return;
      }

      button.disabled = false;
      button.type = 'button';
      button.setAttribute('data-custom-notify-open', popup.id);

      if (label) {
        button.textContent = label;
      }
    });
  }

  function watchProductSection() {
    upgradeStickyBar();

    if (typeof window.MutationObserver !== 'function') {
      return;
    }

    var section = document.querySelector('.shopify-section--main-product');

    if (!section) {
      return;
    }

    var scheduled = false;

    new window.MutationObserver(function () {
      if (scheduled) {
        return;
      }

      scheduled = true;

      window.requestAnimationFrame(function () {
        scheduled = false;

        if (openPopup && !openPopup.isConnected) {
          openPopup = null;
          releaseScrollLock();
        }

        upgradeStickyBar();
      });
    }).observe(section, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchProductSection);
  } else {
    watchProductSection();
  }
})();
